import React, { useState, useMemo, useRef, useEffect } from 'react'
import { createRoot } from 'react-dom/client'

import { Routes, Route, BrowserRouter } from 'react-router-dom'
import './styles.scss'
import Document from 'Documents/publish/components/document'
import SidebarContext from 'Documents/publish/context/sidebarContext'
import { ThemeProvider } from 'styled-components'
import { getTheme } from 'Shared/theme'
import { IntercomProvider } from 'react-use-intercom'
import { HTML5Backend } from 'react-dnd-html5-backend'
import { DndProvider } from 'react-dnd'

/**
 * @typedef {object} Config
 * @property {number} defaultVersionDefaultSectionPath
 * @property {string} defaultVersionPath
 * @property {string} headerColor
 * @property {string} headerTextColor
 * @property {string} slug
 * @property {string} slugPath
 * @property {string} theme
 * @property {string} themeColor
 * @property {string} title
 * @property {string} versionOptions
 * @property {string} versions
 * @property {string} workspaceId
 * @property {string} [logo]
 * @property {string} [intercomId]
 * @property {string} [headers]
 *
 * @typedef {object} Globals
 * @property {Config} [conf]
 *
 * @typedef {Window & Globals} ExtendedWindow
 */

const Index = () => {
  const [responsiveSidebarOpened, setResponsiveSidebarOpened] = useState(false)
  const [config, setConfig] = useState(() => /** @type {ExtendedWindow} */ window.conf)
  const [sections, setSections] = useState(() => /** @type {ExtendedWindow} */ window.sections)
  const [tabSections, setTabSections] = useState(() => /** @type {ExtendedWindow} */ window.tabSections)
  const [section, setSection] = useState(() => /** @type {ExtendedWindow} */ window.section)
  const [environment, setEnvironment] = useState(() => /** @type {ExtendedWindow} */ window.environment)

  // Use useMemo to create the value object
  const value = useMemo(
    () => ({
      responsiveSidebarOpened,
      setResponsiveSidebarOpened
    }),
    [responsiveSidebarOpened]
  )

  useEffect(() => {
    const handleTurboStreamRender = () => {
      const newConfig = /** @type {ExtendedWindow} */ window.conf
      const newSections = /** @type {ExtendedWindow} */ window.sections
      const newTabSections = /** @type {ExtendedWindow} */ window.tabSections
      const newSection = /** @type {ExtendedWindow} */ window.section
      const newEnvironment = /** @type {ExtendedWindow} */ window.environment

      setConfig(newConfig)
      setSections(newSections)
      setTabSections(newTabSections)
      setSection(newSection)
      setEnvironment(newEnvironment)

      if (newConfig && newConfig.theme) {
        document.documentElement.setAttribute('data-bs-theme', newConfig.theme)
      }
    }

    document.addEventListener('turbo:after-stream-render', handleTurboStreamRender)

    return () => {
      document.removeEventListener('turbo:after-stream-render', handleTurboStreamRender)
    }
  }, [])

  return (
    <SidebarContext.Provider value={value}>
      <BrowserRouter>
        <Routes>
          <Route
            path="/:namespace/:workspace/:slug/:version/:section"
            element={
              <IntercomProvider appId={config && config.intercomId ? config.intercomId : ''}>
                <ThemeProvider theme={getTheme(config?.theme ? config.theme : 'light')}>
                  <DndProvider backend={HTML5Backend}>
                    <Document
                      config={config}
                      sections={sections}
                      tabSections={tabSections}
                      section={section}
                      environment={environment}
                    />
                  </DndProvider>
                </ThemeProvider>
              </IntercomProvider>
            }
          />
        </Routes>
      </BrowserRouter>
    </SidebarContext.Provider>
  )
}

export default Index

const root = createRoot(document.getElementById('react'))
root.render(<Index />)
